const { Graph } = require('../../location/graph')
const { listAll } = require('../../location/api/read/list-all')
const findReports = require('./find')
const { listChildren: listLocationChildren } = require('../../location')
const ReportStatus = require('../../tools/utils/progress-statuses')
const { getPeriod, getNextPeriod, getPreviousPeriod } = require('./get-periods')
const { OFFLINE_ERROR } = require('../../utils/offline')
const { isFacilityUser } = require('../../user/tools')

const getFilteredLocations = async (state, graph, locationId, serviceIds) => {
  let parentLocation = await graph.getLocation(locationId)
  let childLocations = await listLocationChildren(state, locationId, {
    filters: {services: serviceIds},
    graph
  })

  if (parentLocation && parentLocation.services.length === 0) {
    parentLocation = null
  }
  childLocations = childLocations.filter(l => l.services.length > 0)

  const locations = (parentLocation ? [parentLocation] : []).concat(childLocations)

  return {
    parentLocation,
    childLocations,
    locations
  }
}

function getStatusOfLocationForService (location, service, reportsMap) {
  let reports = []
  let status = ReportStatus.NOT_STARTED
  const servicesMap = reportsMap.get(location._id)
  if (servicesMap && servicesMap.has(service.id)) {
    reports = servicesMap.get(service.id)
    for (const report of reports) {
      status = report.status
      if (status === ReportStatus.COMPLETE) {
        break
      }
    }
  }
  return Object.assign({}, service, {
    progress: {
      reports,
      status
    }
  })
}

function getStatusOfLocation (location, services, reportsMap) {
  const locationServiceIds = location.services
  const locationServices = services.filter(s => locationServiceIds.includes(s.id))
  const servicesProgress = locationServices.map(s => getStatusOfLocationForService(location, s, reportsMap))

  const total = servicesProgress.length
  const offline = servicesProgress.filter(s => s.progress.status === ReportStatus.OFFLINE).length
  const saved = servicesProgress.filter(s => s.progress.status === ReportStatus.SAVED).length
  const submitted = servicesProgress.filter(s => s.progress.status === ReportStatus.SUBMITTED).length
  const started = servicesProgress.filter(s => s.progress.status !== ReportStatus.NOT_STARTED).length
  const required = servicesProgress.filter(s => !s.isOptional).length
  const complete = servicesProgress.filter(s => s.progress.status === ReportStatus.COMPLETE).length
  const requiredComplete = servicesProgress.filter(s => !s.isOptional && s.progress.status === ReportStatus.COMPLETE).length

  return Object.assign({}, location, {
    canSubmit: true,
    progress: {
      total,
      complete,
      status: requiredComplete >= required ? ReportStatus.COMPLETE
        : offline === total ? ReportStatus.OFFLINE
          : submitted === started && (started > 0) ? ReportStatus.SUBMITTED
            : saved === started && (started > 0) ? ReportStatus.SAVED
              : started > 0 ? ReportStatus.IN_PROGRESS
                : ReportStatus.NOT_STARTED,
      services: servicesProgress
    }
  })
}

async function getReportsStatuses (state, {
  locations,
  locationsIsEntities,
  services,
  startDate,
  endDate,
  config
}) {
  const hasStickyReporting = config && config.features && config.features.stockCount && config.features.stockCount.stickyReportingPeriod

  const result = await findReports(state, {
    locations,
    locationsIsEntities,
    services,
    startDate,
    endDate,
    queryOptions: { include_docs: hasStickyReporting, useCache: true },
    entityOptions: { rawRows: true }
  })

  let rows, docs
  if (hasStickyReporting) {
    rows = result.rows
    docs = result.docs
  } else {
    rows = result
  }

  const locationsMap = new Map()
  for (const [index, row] of rows.entries()) {
    if (row.error && row.error === 'not_found') {
      continue
    }
    let status = row.error && row.error === OFFLINE_ERROR
      ? ReportStatus.OFFLINE
      : ReportStatus.COMPLETE

    if (hasStickyReporting) {
      // date.period.effectiveEnd has a format of YYYY-MM-DD while submittedAt is a full
      // ISO string. That means that any submittedAt timestamp on the effective last
      // day of the period will be > date.period.effectiveEnd.
      if (docs[index].date.period.effectiveEnd > docs[index].submittedAt) {
        status = ReportStatus.SAVED
      } else {
        status = ReportStatus.SUBMITTED
      }
    }

    const locationId = row.locationId
    const serviceId = row.service.id

    const servicesMap = locationsMap.get(locationId) || new Map()
    const reportsList = servicesMap.get(serviceId) || []

    reportsList.push({id: row.key, status})
    servicesMap.set(serviceId, reportsList)
    locationsMap.set(locationId, servicesMap)
  }
  return locationsMap
}

async function checkForIncompleteReports (state, { locations, services, previousPeriod, config, reportsMap }) {
  for (const location of locations) {
    const locationReports = reportsMap.get(location._id)
    if (!locationReports) return true

    for (const service of services) {
      if (!service.isOptional) {
        const serviceReports = locationReports.get(service.id)
        if (!serviceReports || serviceReports.length === 0) return true

        const allReportsSubmitted = serviceReports.every(report =>
          report.status === 'SUBMITTED'
        )
        if (!allReportsSubmitted) return true
      }
    }
  }
  return false
}

module.exports = async (state, {
  locationId,
  program,
  date,
  graph
}, config) => {
  date = new Date(date)
  const activePeriod = await getPeriod(state, {program, date: new Date()})
  const currentPeriod = await getPeriod(state, {program, date})
  const nextPeriod = await getNextPeriod(state, {program, period: currentPeriod})
  const previousPeriod = await getPreviousPeriod(state, {program, period: currentPeriod})

  const periods = {
    active: activePeriod,
    current: currentPeriod,
    next: nextPeriod,
    previous: previousPeriod
  }

  if (!graph) {
    const allLocations = await listAll(state, { date: periods.current.effectiveEndDate })
    graph = new Graph(allLocations)
  }
  const services = program.services
  const serviceIds = services.map(s => s.id)

  const currentPeriodData = await getFilteredLocations(state, graph, locationId, serviceIds)
  let { parentLocation, childLocations, locations } = currentPeriodData
  let reportsMap

  // STICKY PERIOD LOGIC
  const hasStickyReporting = config && config.features && config.features.stockCount && config.features.stockCount.stickyReportingPeriod
  const isFacilityUserCheck = state.user && isFacilityUser(state.user)
  const now = new Date()
  const isInGracePeriod = now >= new Date(previousPeriod.entryEndDate) &&
                          now <= new Date(previousPeriod.entryCutOffDate)

  if (hasStickyReporting && isFacilityUserCheck && isInGracePeriod) {
    try {
      // Get locations that were active in the previous period
      const prevAllLocations = await listAll(state, { date: previousPeriod.effectiveEndDate })
      const prevGraph = new Graph(prevAllLocations)

      const prevPeriodData = await getFilteredLocations(state, prevGraph, locationId, serviceIds)
      const { parentLocation: prevParentLocation, childLocations: prevChildLocations, locations: prevLocations } = prevPeriodData

      if (prevLocations.length > 0) {
        // Get reports for previous period
        const prevReportsMap = await getReportsStatuses(state, {
          locations: prevLocations,
          locationsIsEntities: true,
          services: services,
          startDate: previousPeriod.effectiveStartDate,
          endDate: previousPeriod.effectiveEndDate,
          config
        })

        // Check if previous reports are incomplete
        const hasIncompleteReports = await checkForIncompleteReports(state, {
          locations: prevLocations,
          services,
          previousPeriod,
          config,
          reportsMap: prevReportsMap
        })

        if (hasIncompleteReports) {
          // Override to use previous period data
          locations = prevLocations
          reportsMap = prevReportsMap
          parentLocation = prevParentLocation
          childLocations = prevChildLocations

          periods.current = previousPeriod
          periods.previous = await getPreviousPeriod(state, {program, period: periods.current})
          periods.next = await getNextPeriod(state, {program, period: periods.current})
        }
      }
    } catch (error) {
      console.error('Error in sticky period logic:', error)
    }
  }

  if (!reportsMap) {
    reportsMap = await getReportsStatuses(state, {
      locations,
      locationsIsEntities: true,
      services: services,
      startDate: periods.current.effectiveStartDate,
      endDate: periods.current.effectiveEndDate,
      config
    })
  }

  // Calculate the status of locations and services
  let parent
  if (parentLocation) {
    parent = getStatusOfLocation(parentLocation, services, reportsMap)
  }
  const children = childLocations.map(l => getStatusOfLocation(l, services, reportsMap))
  const all = (parent ? [parent] : []).concat(children)

  // Calculate the total status
  const total = all.length
  const started = all.filter(s => s.progress.status !== ReportStatus.NOT_STARTED).length
  const saved = all.filter(s => s.progress.status === ReportStatus.SAVED).length
  const complete = all.reduce(
    (sum, s) => sum + ([ReportStatus.COMPLETE, ReportStatus.SUBMITTED].includes(s.progress.status) ? 1 : 0),
    0
  )

  const getStatus = (total, started, saved, complete) => {
    if (complete === total && started > 0) {
      return ReportStatus.COMPLETE
    }
    if (saved === started && started > 0) {
      return ReportStatus.SAVED
    }
    if (started > 0) {
      return ReportStatus.IN_PROGRESS
    }
    return ReportStatus.NOT_STARTED
  }

  return Object.assign({}, program, {
    locationId,
    periods,
    progress: {
      total: all.length,
      complete: complete,
      status: getStatus(total, started, saved, complete)
    },
    locations: {
      parent,
      children,
      all
    }
  })
}

module.exports.getStatusOfLocationForService = getStatusOfLocationForService
module.exports.getStatusOfLocation = getStatusOfLocation
module.exports.getReportsStatuses = getReportsStatuses
